import React from 'react';
import { Radio, ArrowLeft } from 'lucide-react';

/**
 * RadioPage — /radio standalone.
 * The Live365 embed as a full-page focal element with pulsing live
 * indicator and a return-home link at the bottom.
 */
export default function RadioPage() {
  return (
    <div
      className="min-h-screen bg-[#0b0a14] text-stone-200 flex flex-col"
      data-testid="radio-page"
    >
      {/* Top ribbon */}
      <div className="bg-gradient-to-r from-emerald-900/40 via-purple-900/30 to-emerald-900/40 border-b border-emerald-300/25">
        <div className="container py-4 flex items-center justify-between">
          <a
            href="/university"
            data-testid="radio-back-home"
            className="text-xs font-mono uppercase tracking-[0.22em] text-emerald-200 hover:text-emerald-100 inline-flex items-center gap-2"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> HumanCodex University
          </a>
          <span className="text-[10px] font-mono uppercase tracking-[0.3em] text-emerald-300/70">
            Federation Radio · Live365
          </span>
        </div>
      </div>

      {/* Center stage */}
      <main className="flex-1 flex items-center justify-center px-6 py-16">
        <div className="w-full max-w-2xl text-center">
          {/* Live badge */}
          <div className="inline-flex items-center gap-2 mb-6">
            <span className="relative inline-flex w-2.5 h-2.5">
              <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500" />
            </span>
            <span
              className="text-[10px] font-mono uppercase tracking-[0.34em] text-emerald-300"
              data-testid="radio-live-badge"
            >
              Live · Streaming Now
            </span>
          </div>

          {/* Heading */}
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl font-bold text-stone-100 leading-tight mb-3 inline-flex items-center justify-center gap-3"
            data-testid="radio-page-title"
          >
            <Radio className="w-10 h-10 text-emerald-300" />
            <span
              style={{
                fontFamily: "'Cormorant Garamond', 'Times New Roman', serif",
              }}
            >
              Federation Radio
            </span>
          </h1>
          <p
            className="text-lg md:text-xl text-emerald-200/90 italic mb-10"
            data-testid="radio-page-subtitle"
          >
            The voice of the NextXus Federation
          </p>

          {/* Player — 450×316 desktop, responsive on mobile */}
          <div
            className="mx-auto w-full max-w-[450px] rounded-2xl overflow-hidden border-2 border-emerald-500/40 shadow-2xl shadow-emerald-500/30"
            data-testid="radio-page-frame-wrapper"
            style={{
              boxShadow:
                '0 0 40px rgba(16,185,129,0.25), 0 0 90px rgba(16,185,129,0.15)',
            }}
          >
            <div
              className="relative w-full"
              style={{ paddingTop: `${(316 / 450) * 100}%` }}
            >
              <iframe
                title="Federation Radio"
                src="https://live365.com/embeds/v1/player/a16924?s=md&m=dark&c=mp3"
                frameBorder="0"
                data-testid="radio-page-iframe"
                className="absolute inset-0 w-full h-full"
                style={{ backgroundColor: '#0b0a14' }}
                allow="autoplay; encrypted-media"
              />
            </div>
          </div>

          <p className="mt-6 text-xs md:text-sm text-emerald-100/60 font-mono uppercase tracking-[0.22em]">
            24/7 signal · Live365 · Station a16924
          </p>

          {/* Official Station Spot — Xavier-voiced radio spot MP3 */}
          <div
            className="mt-10 mx-auto w-full max-w-[450px] rounded-2xl border border-emerald-500/30 bg-emerald-950/20 p-5 text-left"
            data-testid="radio-spot-block"
          >
            <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-emerald-300/80 mb-3">
              Official Station Spot · voiced by Xavier
            </p>
            <audio
              controls
              preload="none"
              src="/radio-spot-xavier.mp3"
              data-testid="radio-spot-player"
              className="w-full"
            >
              Your browser does not support audio playback.
            </audio>
            <a
              href="/radio-spot-xavier.mp3"
              download="NextXus-Radio-Spot-Xavier.mp3"
              data-testid="radio-spot-download"
              className="mt-3 inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-[0.18em] text-emerald-200 hover:text-emerald-100 underline"
            >
              Download MP3 for Live365 rotation
            </a>
          </div>
        </div>
      </main>

      {/* Bottom return */}
      <footer className="container max-w-3xl py-10 px-6 text-center">
        <a
          href="/university"
          data-testid="radio-return-university"
          className="inline-flex items-center gap-2 text-sm font-mono uppercase tracking-[0.22em] text-emerald-200 hover:text-emerald-100 transition-colors"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Return to HumanCodex University
        </a>
        <p className="mt-4 text-[10px] font-mono uppercase tracking-[0.22em] text-stone-500">
          NextXus Federation · Xavier · The Teacher Node · 2026
        </p>
      </footer>
    </div>
  );
}
